import architectureDiagram from "../../Assets/Projects/cyber-range/enterprise-cyber-range-architecture.jpg";
import enterpriseNetworkCover from "../../Assets/Projects/enterprise-network/proxmox-vlan-bridge.png";
import firewallRules from "../../Assets/Projects/enterprise-network/firewall-rules.png";
import opnsenseInterfaces from "../../Assets/Projects/enterprise-network/opnsense-interface-assignments.png";
import opnsenseVlans from "../../Assets/Projects/enterprise-network/opnsense-vlan-configuration.png";

import activeDirectoryCover from "../../Assets/Projects/active-directory/dc01-server-manager.png";
import activeDirectoryUsers from "../../Assets/Projects/active-directory/active-directory-users.png";
import groupPolicy from "../../Assets/Projects/active-directory/group-policy-management.png";
import organizationalUnits from "../../Assets/Projects/active-directory/organizational-units.png";
import win11DomainJoined from "../../Assets/Projects/active-directory/win11-domain-joined.png";

import odooCover from "../../Assets/Projects/odoo-infrastructure/app01-linux-server.png";
import odooCommunity from "../../Assets/Projects/odoo-infrastructure/odoo-community-running.png";
import odooEnterprise from "../../Assets/Projects/odoo-infrastructure/odoo-enterprise-running.png";

import wazuhCover from "../../Assets/Projects/wazuh-monitoring/wazuh-agent-overview.png";
import authenticationAlert from "../../Assets/Projects/wazuh-monitoring/authentication-alert.png";
import sysmonMonitoring from "../../Assets/Projects/wazuh-monitoring/sysmon-event-monitoring.png";
import windowsAgentActive from "../../Assets/Projects/wazuh-monitoring/windows-agent-active.png";

import splunkDashboard from "../../Assets/Projects/splunk-dashboard.png";
import phishingAnalysis from "../../Assets/Projects/phishing-analysis.png";
import suricataPcap from "../../Assets/Projects/suricata-pcap.png";
import wazuhAlert from "../../Assets/Projects/wazuh-alert.png";

const projectsData = [
  {
    id: "enterprise-network",
    title: "Enterprise Network Infrastructure",
    category: "Networking, segmentation and secure access",
    status: "Operational",
    coverImage: architectureDiagram,
    imageClassName: "project-card-image-enterprise",

    summary:
      "Designed and implemented a seven-VLAN enterprise network using Proxmox VE, OPNsense, firewall policies, and secure Tailscale remote access.",

    overview:
      "This project provides the foundation for the enterprise cyber range. OPNsense operates as the central firewall, router, NAT gateway, and VLAN gateway. Proxmox carries tagged traffic through a VLAN-aware Linux bridge, while a dedicated Ubuntu Tailscale subnet router provides encrypted remote access to approved internal systems.",

    highlights: [
      "Designed and implemented seven isolated network segments",
      "Configured OPNsense routing, NAT, VLAN gateways, and firewall policies",
      "Created a VLAN-aware Proxmox Linux bridge for 802.1Q traffic",
      "Deployed TS-ROUTER01 as a dedicated Tailscale subnet router",
      "Advertised approved internal subnets to authorized remote devices",
      "Kept Proxmox, OPNsense, Active Directory, Wazuh, and internal applications off the public internet"
    ],

    architecture: [
      "Internet traffic enters through the ISP or home gateway.",
      "OPNsense provides NAT, routing, firewall enforcement, and VLAN gateways.",
      "A VLAN-aware Proxmox Linux bridge carries the 802.1Q trunk.",
      "Virtual machines and enterprise services are assigned to isolated VLANs.",
      "TS-ROUTER01 advertises approved internal routes through Tailscale.",
      "Authorized devices use an encrypted WireGuard-based overlay to reach permitted systems."
    ],

    technologies: [
      "Proxmox VE",
      "OPNsense",
      "802.1Q VLANs",
      "Linux Bridges",
      "Firewall Policies",
      "Inter-VLAN Routing",
      "Tailscale",
      "WireGuard",
      "Subnet Routing"
    ],

    remoteAccess: {
      description:
        "An Ubuntu-based Tailscale subnet router advertises selected lab networks to authorized devices. Remote administration is encrypted, device-authorized, and does not require exposing management interfaces to the public internet.",

      flow: [
        "Authorized admin device",
        "Tailscale encrypted overlay",
        "TS-ROUTER01 subnet router",
        "Approved internal routes",
        "Permitted lab systems"
      ]
    },

    evidence: [
      {
        image: architectureDiagram,
        title: "Complete enterprise architecture",
        description:
          "Proxmox virtualization, OPNsense network control, seven VLANs, enterprise services, and Tailscale remote access."
      },
      {
        image: enterpriseNetworkCover,
        title: "Proxmox VLAN-aware bridge",
        description:
          "Linux bridge configured to carry tagged traffic between OPNsense and virtual machines."
      },
      {
        image: opnsenseInterfaces,
        title: "OPNsense interface assignments",
        description:
          "WAN and VLAN interfaces assigned to their intended network roles."
      },
      {
        image: opnsenseVlans,
        title: "Seven-VLAN configuration",
        description:
          "Infrastructure, server, user, security, voice, guest, and IoT networks configured as separate segments."
      },
      {
        image: firewallRules,
        title: "Firewall policy configuration",
        description:
          "Rules controlling communication between internal network segments."
      }
    ],

    repository:
      "https://github.com/stonyango294/Stephen-Enterprise-Cyber-Range"
  },

  {
    id: "active-directory",
    title: "Active Directory Environment",
    category: "Identity and systems administration",
    status: "Operational",
    coverImage: activeDirectoryCover,

    summary:
      "Deployed a Windows Server domain environment for centralized identity, authentication, policy management, and endpoint administration.",

    overview:
      "DC01 provides Active Directory Domain Services and DNS for the enterprise environment. The implementation includes users, groups, organizational units, Group Policy, and a Windows 11 endpoint joined to the domain.",

    highlights: [
      "Deployed Windows Server as the domain controller",
      "Configured Active Directory Domain Services and DNS",
      "Created users, groups, and organizational units",
      "Configured and tested Group Policy",
      "Joined a Windows 11 endpoint to the domain"
    ],

    architecture: [
      "DC01 provides Active Directory Domain Services and DNS.",
      "Domain endpoints use DC01 for authentication and name resolution.",
      "Users and computers are organized through organizational units.",
      "Group Policy provides centralized configuration and security control."
    ],

    technologies: [
      "Windows Server",
      "Active Directory",
      "DNS",
      "Group Policy",
      "Windows 11",
      "PowerShell"
    ],

    evidence: [
      {
        image: activeDirectoryCover,
        title: "DC01 Server Manager",
        description:
          "Windows Server hosting Active Directory Domain Services and DNS."
      },
      {
        image: activeDirectoryUsers,
        title: "Active Directory users and computers",
        description:
          "Centralized identity objects created for users, groups, and computers."
      },
      {
        image: organizationalUnits,
        title: "Organizational unit structure",
        description:
          "Logical structure used to organize users, computers, and administrative policies."
      },
      {
        image: groupPolicy,
        title: "Group Policy management",
        description:
          "Centralized configuration and security policies applied to domain systems."
      },
      {
        image: win11DomainJoined,
        title: "Domain-joined Windows 11 endpoint",
        description:
          "Windows 11 endpoint successfully connected to the enterprise domain."
      }
    ],

    repository:
      "https://github.com/stonyango294/Stephen-Enterprise-Cyber-Range"
  },

  {
    id: "odoo-infrastructure",
    title: "Enterprise Linux and Odoo Platform",
    category: "Linux and business applications",
    status: "Operational",
    coverImage: odooCover,

    summary:
      "Built and administered a Linux application server hosting separate Odoo Community and Enterprise environments.",

    overview:
      "APP01 provides a dedicated Linux environment for enterprise application administration and testing. It hosts Odoo Community and Enterprise instances for ERP configuration, workflow testing, troubleshooting, and continued development.",

    highlights: [
      "Configured APP01 as a dedicated Linux application server",
      "Deployed Odoo Community and Enterprise environments",
      "Configured separate application services",
      "Validated access to both environments",
      "Created a platform for ERP administration and testing"
    ],

    architecture: [
      "APP01 operates inside the protected server VLAN.",
      "Linux services host separate Odoo Community and Enterprise instances.",
      "PostgreSQL provides the application database layer.",
      "The environment supports ERP administration, testing, and development."
    ],

    technologies: [
      "Ubuntu Server",
      "Odoo Community",
      "Odoo Enterprise",
      "PostgreSQL",
      "Python",
      "Linux Services"
    ],

    evidence: [
      {
        image: odooCover,
        title: "APP01 Linux application server",
        description:
          "Dedicated Linux server used to host enterprise business applications."
      },
      {
        image: odooCommunity,
        title: "Odoo Community environment",
        description:
          "Community edition deployed for development and application testing."
      },
      {
        image: odooEnterprise,
        title: "Odoo Enterprise environment",
        description:
          "Enterprise edition deployed for ERP administration and workflow testing."
      }
    ],

    repository: "https://github.com/stonyango294"
  },

  {
    id: "wazuh-monitoring",
    title: "Security Monitoring Platform",
    category: "Defensive security",
    status: "Active",
    coverImage: wazuhCover,

    summary:
      "Implemented centralized endpoint monitoring using Wazuh, Sysmon, Windows security events, and Linux telemetry.",

    overview:
      "SEC01 provides centralized security visibility across Windows and Linux systems. Wazuh agents collect endpoint events, while Sysmon adds detailed Windows process and network telemetry for detection and investigation.",

    highlights: [
      "Deployed SEC01 as the centralized Wazuh platform",
      "Onboarded Windows and Linux endpoints",
      "Integrated Sysmon endpoint telemetry",
      "Monitored authentication and process activity",
      "Created a foundation for detection rules and incident simulations"
    ],

    architecture: [
      "SEC01 hosts the centralized Wazuh manager and dashboard.",
      "Agents collect security events from Windows and Linux endpoints.",
      "Sysmon provides detailed Windows process and network telemetry.",
      "Events are reviewed centrally for investigation and detection development."
    ],

    technologies: [
      "Wazuh",
      "Sysmon",
      "Windows Event Logs",
      "Ubuntu",
      "Windows 11",
      "MITRE ATT&CK"
    ],

    evidence: [
      {
        image: wazuhCover,
        title: "Wazuh agent overview",
        description:
          "Centralized dashboard showing connected and monitored endpoint agents."
      },
      {
        image: windowsAgentActive,
        title: "Windows agent connected",
        description:
          "Windows endpoint registered and communicating with SEC01."
      },
      {
        image: sysmonMonitoring,
        title: "Sysmon event monitoring",
        description:
          "Detailed Windows process and endpoint activity available for investigation."
      },
      {
        image: authenticationAlert,
        title: "Authentication security alert",
        description:
          "Authentication activity detected and reviewed through Wazuh."
      }
    ],

    repository:
      "https://github.com/stonyango294/wazuh-edr-lab"
  },

  {
    id: "soc-investigations",
    title: "SOC Investigations",
    category: "Threat detection and incident response",
    status: "Expanding",
    coverImage: splunkDashboard,

    summary:
      "Performed practical investigations involving failed logins, phishing activity, endpoint alerts, and suspicious network traffic.",

    overview:
      "This work brings together log analysis, alert triage, IOC validation, packet inspection, and incident documentation using endpoint, SIEM, sandbox, and network evidence.",

    highlights: [
      "Investigated repeated Windows authentication failures",
      "Performed phishing analysis and IOC extraction",
      "Reviewed suspicious endpoint and persistence activity",
      "Validated network activity through packet analysis",
      "Mapped selected activity to MITRE ATT&CK"
    ],

    architecture: [
      "Windows, Linux, and network systems produce security telemetry.",
      "Splunk and Wazuh support centralized investigation and event review.",
      "Sandbox and reputation services support IOC validation.",
      "Wireshark and Suricata provide network-level evidence."
    ],

    technologies: [
      "Splunk",
      "Wazuh",
      "Sysmon",
      "Wireshark",
      "Suricata",
      "VirusTotal",
      "ANY.RUN"
    ],

    evidence: [
      {
        image: splunkDashboard,
        title: "Splunk security dashboard",
        description:
          "Dashboard used to investigate authentication and security events."
      },
      {
        image: phishingAnalysis,
        title: "Phishing investigation",
        description:
          "Email and attachment analysis used to identify suspicious indicators."
      },
      {
        image: wazuhAlert,
        title: "Endpoint security alert",
        description:
          "Wazuh alert reviewed during endpoint threat investigation."
      },
      {
        image: suricataPcap,
        title: "Network traffic analysis",
        description:
          "Packet and IDS evidence reviewed during network investigation."
      }
    ],

    repository: "https://github.com/stonyango294"
  }
];

export const getProjectById = (projectId) =>
  projectsData.find(
    (project) => project.id === projectId
  );

export default projectsData;